import { useEffect, useState } from "react";
import { ExternalLink, PenLine } from "lucide-react";
import { useSiteData } from "@/context/SiteDataContext";
import { api } from "@/lib/api";
import { Stars } from "./Stars";

export const useGoogleLinks = () => {
  const { settings } = useSiteData();
  const write = settings.review_link || "";
  return { write, all: write.replace(/\/review\/?$/, "") };
};

export const GoogleIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

export const GoogleButtons = ({ light, prefix = "google" }) => {
  const { write, all } = useGoogleLinks();
  if (!write) return null;
  return (
    <div className="flex flex-wrap xl:flex-nowrap gap-3 shrink-0">
      <a href={all} target="_blank" rel="noreferrer" data-testid={`${prefix}-view-all`}
        className={`inline-flex items-center gap-2 h-12 px-5 sm:px-6 rounded-full font-semibold whitespace-nowrap text-sm sm:text-base transition-colors ${light ? "bg-white text-navy hover:bg-aqua" : "bg-ocean text-white hover:bg-navy"}`}>
        <GoogleIcon className="h-5 w-5 bg-white rounded-full p-0.5" /> View All Reviews on Google
      </a>
      <a href={write} target="_blank" rel="noreferrer" data-testid={`${prefix}-write`}
        className={`inline-flex items-center gap-2 h-12 px-5 sm:px-6 rounded-full font-semibold whitespace-nowrap text-sm sm:text-base border transition-colors ${light ? "border-white/40 text-white hover:bg-white/10" : "border-slate-300 bg-white text-navy hover:border-ocean hover:text-ocean"}`}>
        <PenLine className="h-4 w-4" /> Write a Review
      </a>
    </div>
  );
};

const date = (r) => r.publish_time ? new Date(r.publish_time).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : r.relative_time;

const GoogleReviewCard = ({ r, i }) => (
  <article className="rounded-2xl border border-slate-200 bg-white p-6 flex flex-col" data-testid={`google-review-${i}`}>
    <div className="flex items-center gap-3">
      {r.author_photo_uri ? <img src={r.author_photo_uri} alt="" referrerPolicy="no-referrer" className="h-10 w-10 rounded-full object-cover" />
        : <span className="h-10 w-10 rounded-full bg-sky-100 text-ocean grid place-items-center font-semibold">{(r.author_name || "?")[0]}</span>}
      <div className="min-w-0 flex-1">
        {r.author_uri ? <a href={r.author_uri} target="_blank" rel="noreferrer" className="block font-semibold text-navy truncate hover:text-ocean">{r.author_name}</a>
          : <p className="font-semibold text-navy truncate">{r.author_name}</p>}
        <p className="text-xs text-slate-500">{date(r)}</p>
      </div>
      <GoogleIcon className="h-5 w-5 shrink-0" />
    </div>
    <div className="mt-4"><Stars value={r.rating || 0} /></div>
    <p className="mt-3 text-sm text-slate-600 leading-relaxed whitespace-pre-line flex-1">{r.text}</p>
    {r.google_maps_uri && <a href={r.google_maps_uri} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-ocean hover:text-navy">View on Google Maps <ExternalLink className="h-3 w-3" /></a>}
  </article>
);

const Pending = () => (
  <div className="rounded-3xl border border-sky-100 bg-mist p-8 sm:p-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6" data-testid="google-reviews-pending">
    <div className="flex items-start gap-4">
      <span className="h-14 w-14 shrink-0 rounded-2xl bg-white grid place-items-center shadow-sm"><GoogleIcon className="h-7 w-7" /></span>
      <div>
        <h2 className="font-display text-3xl text-navy font-semibold">Read our reviews on Google</h2>
        <p className="mt-1 text-slate-600 max-w-xl">See what travellers say about Ocean Wings Travel on our Google Business Profile, or leave a review of your own trip.</p>
      </div>
    </div>
    <GoogleButtons prefix="google-pending" />
  </div>
);

export const GoogleReviews = () => {
  const [g, setG] = useState(null);
  useEffect(() => { api.get("/google-reviews").then((r) => setG(r.data)).catch(() => setG({ configured: false })); }, []);
  if (!g) return null;
  if (!g.configured || g.error || !g.rating) return <Pending />;
  return (
    <div data-testid="google-reviews">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 flex flex-col lg:flex-row lg:items-center justify-between gap-6" data-testid="google-rating-summary">
        <div className="flex items-center gap-5">
          <span className="h-16 w-16 shrink-0 rounded-2xl bg-mist grid place-items-center"><GoogleIcon className="h-8 w-8" /></span>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Google rating</p>
            <div className="mt-1 flex items-center gap-3 flex-wrap">
              <span className="font-mono text-4xl font-semibold text-navy" data-testid="google-rating">{g.rating.toFixed(1)}</span>
              <Stars value={g.rating} size="h-5 w-5" />
            </div>
            <p className="text-sm text-slate-500" data-testid="google-review-count">Based on {g.count} Google review{g.count === 1 ? "" : "s"}</p>
          </div>
        </div>
        <GoogleButtons prefix="google" />
      </div>
      {g.reviews.length > 0 && (
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{g.reviews.map((r, i) => <GoogleReviewCard key={r.google_maps_uri || i} r={r} i={i} />)}</div>
      )}
      <p className="mt-4 text-xs text-slate-500">Reviews from Google Maps, ordered by relevance by Google.</p>
    </div>
  );
};
